import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import type { SubscriptionPeriod } from '../components/subscription/SubscriptionCard';
import type { PlanId } from '../types/subscription';

type CompanyInput = { companyId: string };
type SessionInput = CompanyInput & { sessionId: string };
type SelectionInput = CompanyInput & {
  plan: PlanId;
  period: SubscriptionPeriod;
};
type CheckoutResult = { sessionId: string; url: string };
export type ConfirmationResult = {
  status: 'open' | 'complete' | 'expired' | null;
  plan?: PlanId;
  period?: SubscriptionPeriod;
};
export type ResumeResult = {
  subscriptionId: string;
  status: string;
  paymentUrl: string | null;
};

async function call<Input, Output>(
  name: string,
  input: Input
): Promise<Output> {
  const result = await httpsCallable<Input, Output>(functions, name)(input);
  return result.data;
}

export function redirectToBilling(url: string) {
  const destination = new URL(url);
  if (destination.protocol !== 'https:')
    throw new Error('Unable to open secure billing.');
  window.location.assign(destination.toString());
}

export async function purchaseSubscription(
  companyId: string,
  plan: PlanId,
  period: SubscriptionPeriod
) {
  const result = await call<SelectionInput, CheckoutResult>(
    'createSubscriptionCheckout',
    { companyId, plan, period }
  );
  sessionStorage.setItem(`billing-company-${result.sessionId}`, companyId);
  redirectToBilling(result.url);
}

export function confirmSubscriptionCheckout(
  companyId: string,
  sessionId: string
) {
  return call<SessionInput, ConfirmationResult>('confirmSubscriptionCheckout', {
    companyId,
    sessionId,
  });
}

export function cancelSubscriptionCheckout(
  companyId: string,
  checkoutId: string
) {
  return call<CompanyInput & { checkoutId: string }, { status: 'expired' }>(
    'cancelSubscriptionCheckout',
    { companyId, checkoutId }
  );
}

export async function openSubscriptionPortal(companyId: string) {
  const result = await call<CompanyInput, { url: string }>(
    'createSubscriptionPortal',
    { companyId }
  );
  redirectToBilling(result.url);
}

export async function startSubscriptionPaymentSetup(companyId: string) {
  const result = await call<CompanyInput, CheckoutResult>(
    'createSubscriptionPaymentCheckout',
    { companyId }
  );
  sessionStorage.setItem(`billing-company-${result.sessionId}`, companyId);
  redirectToBilling(result.url);
}

export function resumeSubscription(companyId: string, sessionId: string) {
  return call<SessionInput, ResumeResult>('resumeSubscription', {
    companyId,
    sessionId,
  });
}

export function changeSubscriptionPlan(
  companyId: string,
  plan: PlanId,
  period: SubscriptionPeriod,
  requestId: string
) {
  return call<
    SelectionInput & { requestId: string },
    { subscriptionId: string; status: string }
  >('changeSubscriptionPlan', { companyId, plan, period, requestId });
}

export function cancelSubscription(companyId: string, requestId: string) {
  return call<
    { companyId: string; requestId: string },
    { subscriptionId: string; status: string; cancelAtPeriodEnd: boolean }
  >('cancelSubscription', { companyId, requestId });
}

export function restoreSubscription(companyId: string, requestId: string) {
  return call<
    { companyId: string; requestId: string },
    { subscriptionId: string; status: string }
  >('restoreSubscription', { companyId, requestId });
}

export function checkoutSelectionFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const plan = params.get('plan');
  return {
    plan: (['starter', 'professional', 'business'].includes(plan ?? '')
      ? plan
      : 'professional') as PlanId,
    period: (params.get('period') === 'yearly'
      ? 'yearly'
      : 'monthly') as SubscriptionPeriod,
  };
}
